import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { images } from '../../data/mock';
import {
  Satellite,
  Waves,
  Cpu,
  BarChart,
  ArrowRight,
  Zap,
  Thermometer,
  Globe,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const TechnologyPage = () => {
  const { t, isRTL } = useLanguage();

  // Section B: Implementation process steps (we'll render 4 steps below)
  const processSteps = [
    {
      icon: Satellite,
      title: t('technology.step1NewTitle'),
      desc: t('technology.step1NewDesc'),
      step: '01',
    },
    {
      icon: Waves,
      title: t('technology.step2NewTitle'),
      desc: t('technology.step2NewDesc'),
      step: '02',
    },
    {
      icon: Cpu,
      title: t('technology.step3NewTitle'),
      desc: t('technology.step3NewDesc'),
      step: '03',
    },
    {
      icon: BarChart,
      title: t('technology.step4NewTitle'),
      desc: t('technology.step4NewDesc'),
      step: '04',
    },
  ];

  const specs = [
    {
      icon: Zap,
      title: t('technology.spec1Title'),
      desc: t('technology.spec1Desc'),
    },
    {
      icon: Thermometer,
      title: t('technology.spec2Title'),
      desc: t('technology.spec2Desc'),
    },
    {
      icon: Globe,
      title: t('technology.spec3Title'),
      desc: t('technology.spec3Desc'),
    },
  ];

  return (
    <div className="min-h-screen bg-black pt-20" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section
        className="relative py-20"
        style={{
          backgroundImage: `url('/images/EMAF_BG.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('technology.pageTitle')}
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              {t('technology.intro')}
            </p>
          </div>
        </div>
      </section>

      {/* Section A: The Method of Work (3 Feature Cards) */}
      <section className="py-24 bg-zinc-950">
        <style>{`
          /* slower, more visible red-tinted animations */
          @keyframes soft-pulse {
            0% { box-shadow: 0 0 0 0 rgba(220,38,38,0.00); }
            50% { box-shadow: 0 10px 40px 10px rgba(220,38,38,0.10); }
            100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.00); }
          }

          @keyframes subtle-shimmer {
            0% { background-position: -250% 0; }
            100% { background-position: 250% 0; }
          }

          /* underline fade (unchanged) */
          .fading-underline {
            height: 2px;
            background: linear-gradient(90deg, rgba(220,38,38,0.85), rgba(220,38,38,0.6));
            -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%);
            mask-image: linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%);
            opacity: 0.9;
          }

          /* card shimmer now has a subtle red tint and moves slower */
          .card-shimmer {
            background-image: linear-gradient(
              90deg,
              rgba(220,38,38,0.00) 0%,
              rgba(220,38,38,0.03) 40%,
              rgba(255,255,255,0.00) 50%,
              rgba(220,38,38,0.02) 60%,
              rgba(220,38,38,0.00) 100%
            );
            background-size: 300% 100%;
            animation: subtle-shimmer 14s linear infinite;
            pointer-events: none;
            border-radius: inherit;
            mix-blend-mode: screen;
            opacity: 0.9;
          }

          /* icon pulse: slower and uses the same red color */
          .icon-pulse {
            animation: soft-pulse 6s ease-in-out infinite;
            /* subtle inner glow using the same red */
            box-shadow: 0 0 0 0 rgba(220,38,38,0);
            transition: box-shadow 0.3s ease;
          }

          /* intensify on hover but still restrained */
          .card-hover:hover {
            transform: translateY(-6px);
            box-shadow: 0 14px 40px rgba(0,0,0,0.6);
            border-color: rgba(220,38,38,0.28);
          }

          /* ensure the shimmer doesn't overwhelm text */
          .card-content { position: relative; z-index: 10; }
        `}</style>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">{t('technology.methodTitle')}</h2>

          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {specs.map((s, idx) => (
              <div
                key={idx}
                className="relative p-6 bg-zinc-900/60 border border-zinc-800 rounded-2xl transition-all card-hover"
              >
                {/* subtle red shimmer overlay (decorative) */}
                <div className="absolute inset-0 card-shimmer rounded-2xl" aria-hidden="true" />

                <div className="absolute -top-4 left-6 px-3 py-1 bg-[rgb(220,38,38)]/95 rounded-lg text-white text-sm font-bold">
                  {/* optional tag area */}
                </div>

                <div className="flex items-start gap-4 card-content">
                  <div className="w-12 h-12 bg-[rgba(220,38,38,0.06)] rounded-lg flex items-center justify-center flex-shrink-0">
                    {/* icon with visible red pulse and slightly darker red color */}
                    <s.icon className="h-6 w-6 text-[rgb(150,20,20)] icon-pulse" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{s.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Fading underline centered under the cards */}
          <div className="mt-9 flex justify-center" aria-hidden="true">
            <div className="w-full max-w-3xl fading-underline" />
          </div>
        </div>
      </section>


      {/* Section B: The Implementation Method (4-step process) */}
      <section className="pt-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">{t('technology.implementationTitle')}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-6">
              {processSteps.map((step, idx) => (
                <div className="relative" key={idx}>
                  <div className="absolute -top-4 left-6 px-3 py-1 bg-red-600 rounded-lg text-white text-sm font-bold">
                    {step.step}
                  </div>
                  <div className="h-full p-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-red-500/30 transition-all">
                    <div className="w-14 h-14 bg-red-500/10 rounded-lg flex items-center justify-center mb-4">
                      <step.icon className="h-7 w-7 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Two images stacked vertically (same style) */}
            <div className="mx-auto flex flex-col items-center gap-4" style={{ maxWidth: '75%' }}>
              <img
                src="./images/layers.png"
                alt="Layers"
                className="rounded-2xl shadow-[0_0_15px_5px_rgba(255,0,0,0.3)] border-2 border-red-500/50 w-full h-auto object-contain"
              />

              <img
                src="./images/heatmaps.png"
                alt="Heatmaps"
                className="rounded-2xl shadow-[0_0_15px_5px_rgba(255,0,0,0.3)] border-2 border-red-500/50 w-full h-auto object-contain"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">
            {t('technology.comparisonTitle')}
          </h2>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-red-400 font-semibold py-6 text-center">
                    {t('technology.tableFeature')}
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold py-6 text-center">
                    {t('technology.tableSeismic')}
                  </TableHead>
                  <TableHead className="text-gray-200 font-semibold py-6 text-center">
                    {t('technology.tableEmaf')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="border-zinc-800">
                  <TableCell className="text-red-400 font-semibold py-6 text-center">
                    {t('technology.rowDetects')}
                  </TableCell>
                  <TableCell className="text-gray-400 py-6 text-center">
                    {t('technology.rowDetectsSeismic')}
                  </TableCell>
                  <TableCell className="text-gray-200 py-6 text-center">
                    {t('technology.rowDetectsEmaf')}
                  </TableCell>
                </TableRow>
                <TableRow className="border-zinc-800">
                  <TableCell className="text-red-400 font-semibold py-6 text-center">
                    {t('technology.rowRisk')}
                  </TableCell>
                  <TableCell className="text-gray-400 py-6 text-center">
                    {t('technology.rowRiskSeismic')}
                  </TableCell>
                  <TableCell className="text-green-400 py-6 text-center">
                    {t('technology.rowRiskEmaf')}
                  </TableCell>
                </TableRow>
                <TableRow className="border-zinc-800">
                  <TableCell className="text-red-400 font-semibold py-6 text-center">
                    {t('technology.rowAccess')}
                  </TableCell>
                  <TableCell className="text-gray-400 py-6 text-center">
                    {t('technology.rowAccessSeismic')}
                  </TableCell>
                  <TableCell className="text-gray-200 py-6 text-center">
                    {t('technology.rowAccessEmaf')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyPage;